/**
 * Page Context
 *
 * Allows any page to publish structured context about what the user is
 * currently viewing (e.g. a specific PO, its issues, its status).
 * ChatWidget reads this context and sends it alongside every chat message
 * so the agent knows what the user is looking at without being told.
 *
 * Pages call setPageContext({...}) on mount/data-load and clearPageContext()
 * on unmount. Components that consume context (ChatWidget) just read the
 * value — they never write to it.
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';

const PageContext = createContext(null);

/**
 * Hook to read the current page context value.
 * Returns { pageContext, setPageContext, clearPageContext }.
 */
export function usePageContext() {
  const ctx = useContext(PageContext);
  if (!ctx) {
    throw new Error('usePageContext must be used within a PageContextProvider');
  }
  return ctx;
}

/**
 * Provider — wrap this around the component tree (inside MainLayout or App).
 */
export function PageContextProvider({ children }) {
  const [pageContext, setPageContextState] = useState(null);

  const setPageContext = useCallback((context) => {
    setPageContextState(context);
  }, []);

  const clearPageContext = useCallback(() => {
    setPageContextState(null);
  }, []);

  const value = useMemo(
    () => ({ pageContext, setPageContext, clearPageContext }),
    [pageContext, setPageContext, clearPageContext]
  );

  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
}
